import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select, { components } from 'react-select'
import { Footer, Header } from './headerFooter';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import { getHost } from './config';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./custom.scss";

const RENT_PRICE_COLUMNS = [
    "Α' τρίμηνο 2024 (€/τμ)",
    "Α' τρίμηνο 2025 (€/τμ)",
    "Α' τρίμηνο 2026 (€/τμ)",
];

function formatRentCell(value) {
    return value === 0 || value == null ? '-' : value;
}

const RentPrices = () => {
    const [data, setData] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [uniqueLocations, setUniqueLocations] = useState([]);
    const [sortBy, setSortBy] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc'); // ascending order by default
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
        }
    }, [token, navigate]);

    const handleLogout = () => {
        // Clear the authToken from localStorage
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());

        // Redirect to the login page
        navigate('/', { replace: true });
    }

    useEffect(() => {
        // Fetch data from the backend
        const url = `${getHost()}/api/rent-prices`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(response => response.json())
            .then(data => {
                setData(data.results);
                setFilteredData(data.results);
                const locations = [...new Set(data.results.map(item => item['Περιοχή']))];
                setUniqueLocations(locations.map(location => ({ value: location, label: location })));
            });
    }, []);

    useEffect(() => {
        if (selectedRegions.length === 0) {
            setFilteredData(data);
        } else {
            const filtered = data.filter(item => selectedRegions.some(region => region.value === item['Περιοχή']));
            setFilteredData(filtered);
        }
    }, [selectedRegions, data]);

    const handleRegionChange = (selectedOptions) => {
        setSelectedRegions(selectedOptions);
    };

    const handleSort = (column) => {
        const order = column === sortBy && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortBy(column);
        setSortOrder(order);
        const sortedData = [...filteredData].sort((a, b) => {
            const aVal = a[column];
            const bVal = b[column];
            if (column === 'Περιοχή') {
                const cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''), 'el');
                return order === 'asc' ? cmp : -cmp;
            }
            const aNum = Number(aVal) || 0;
            const bNum = Number(bVal) || 0;
            return order === 'asc' ? aNum - bNum : bNum - aNum;
        });
        setFilteredData(sortedData);
    };

    const renderSortableHeader = (column, label = column) => {
        const isActive = sortBy === column;
        const iconClass = !isActive
            ? 'bi-arrow-down-up sortable-col-icon sortable-col-icon--inactive'
            : sortOrder === 'asc'
                ? 'bi-caret-up-fill sortable-col-icon'
                : 'bi-caret-down-fill sortable-col-icon';

        return (
            <th
                key={column}
                className="sortable"
                onClick={() => handleSort(column)}
                aria-sort={
                    isActive
                        ? sortOrder === 'asc'
                            ? 'ascending'
                            : 'descending'
                        : 'none'
                }
            >
                <span className="sortable-col-head">
                    <span>{label}</span>
                    <i className={`bi ${iconClass}`} aria-hidden />
                </span>
            </th>
        );
    };

    // Custom Option component to add checkboxes
    const Option = (props) => {
        return (
            <components.Option {...props}>
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    style={{
                        marginRight: 10,
                        accentColor: props.isSelected ? 'white' : 'initial', // Change color when selected
                    }}
                />{' '}
                <label>{props.label}</label>
            </components.Option>
        );
    };

    // Custom styles for react-select components
    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#4caf50' : state.isFocused ? '#f0f0f0' : 'white',
            color: state.isSelected ? 'white' : 'black',
            boxShadow: state.isFocused ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none',
            '&:hover': {
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Add light shadow on hover
            },
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ccc',
            '&:hover': {
                borderColor: '#b3b3b3',
            },
        }),
        checkbox: (provided) => ({
            ...provided,
            backgroundColor: 'black', // Change checkbox color to black
        }),
    };

    
    return (
        <div id="root">
            <Header handleLogout={handleLogout}/>
            <main className='main-container ranking-per-state-page'>
                <div className="ranking-per-state-main rent-prices-main">
                    <Container fluid className="ranking-per-state-container">
                        <Row className="g-3 g-md-4">
                            <Col xs={12}>
                                <div className="ranking-per-state-content">
                                    {data ? (
                                        <div>
                                            <div className="d-flex align-items-center my-3">
                                                <h5 className="text-center flex-grow-1 m-0">Αποτελέσματα</h5>
                                            </div>
                                            <div className='table-outer-wrapper'>
                                                <div className="table-scroll-wrapper">
                                                    <Table bordered className="historical-results-table">
                                                        <thead>
                                                            <tr>
                                                                {renderSortableHeader('Περιοχή')}
                                                                {RENT_PRICE_COLUMNS.map((column) => renderSortableHeader(column))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredData && filteredData.map((item, index) => (
                                                                <tr key={index}>
                                                                    <td>{item['Περιοχή']}</td>
                                                                    {RENT_PRICE_COLUMNS.map((column) => (
                                                                        <td key={column}>{formatRentCell(item[column])}</td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <p>Φόρτωση...</p>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default RentPrices;